import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Plus, Minus } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { StockAlertForm } from "@/components/StockAlertForm";

const gbp = (pence) => `£${(pence / 100).toFixed(2)}`;

export const ProductModal = ({ product, tint, open, onClose }) => {
  const { items, addItem, setQuantity } = useCart();
  if (!product) return null;
  const inCart = items.find((i) => i.lookup_key === product.lookup_key)?.quantity || 0;
  const soldOut = product.stock !== null && product.stock !== undefined && product.stock <= 0;
  const comingSoon = !!product.coming_soon;
  const nutrition = product.nutrition;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="sm:max-w-xl rounded-[28px] border-0 p-0 overflow-hidden max-h-[90vh] overflow-y-auto bg-[#FBF6EC]"
        data-testid="product-modal"
      >
        <div className="h-52 sm:h-64 flex items-center justify-center" style={{ backgroundColor: tint }}>
          <img
            src={product.image}
            alt={product.name}
            className="h-full w-full object-contain p-6 mix-blend-multiply"
            data-testid="modal-image"
          />
        </div>
        <div className="p-6 sm:p-8 space-y-7">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#4A148C] mb-1">{product.tag}</p>
              <DialogTitle className="font-display text-3xl font-bold tracking-tight text-[#2B0B3C]" data-testid="modal-name">
                {product.name}
              </DialogTitle>
              <p className="text-sm font-medium text-[#2B0B3C]/60 mt-2">{product.strap}</p>
              <p className="mt-3 font-display text-2xl font-bold text-[#2B0B3C]" data-testid="modal-price">
                {gbp(product.price_pence)}
              </p>
            </div>
            {inCart > 0 && !soldOut && !comingSoon ? (
              <div className="shrink-0 mt-1 inline-flex items-center bg-[#2B0B3C] text-white rounded-full" data-testid="modal-stepper">
                <button
                  onClick={() => setQuantity(product.lookup_key, inCart - 1)}
                  aria-label={`Remove one ${product.name}`}
                  data-testid="modal-decrease"
                  className="w-11 h-11 flex items-center justify-center rounded-full hover:text-[#FFD600] transition-colors duration-200"
                >
                  <Minus size={16} strokeWidth={3} />
                </button>
                <span className="min-w-7 text-center font-display font-bold" data-testid="modal-qty">{inCart}</span>
                <button
                  onClick={() => addItem(product, false)}
                  aria-label={`Add one more ${product.name}`}
                  data-testid="modal-increase"
                  className="w-11 h-11 flex items-center justify-center rounded-full hover:text-[#FFD600] transition-colors duration-200"
                >
                  <Plus size={16} strokeWidth={3} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => {
                  addItem(product, false);
                  toast(`${product.name} added to your basket`);
                }}
                disabled={soldOut || comingSoon}
                data-testid="modal-add-to-cart"
                aria-label={soldOut ? `${product.name} is sold out` : comingSoon ? `${product.name} is coming soon` : `Add ${product.name} to basket`}
                className={`shrink-0 mt-1 rounded-full px-6 h-12 font-display font-bold text-sm uppercase tracking-wide transition-colors duration-200 ${
                  soldOut || comingSoon ? "bg-[#2B0B3C]/10 text-[#2B0B3C]/30 cursor-not-allowed" : "bg-[#2B0B3C] text-white hover:bg-[#FF6B00]"
                }`}
              >
                {soldOut ? "Sold out" : comingSoon ? "Coming soon" : "Add to basket"}
              </button>
            )}
          </div>

          {(soldOut || comingSoon) && <StockAlertForm lookupKey={product.lookup_key} />}

          {product.benefits && (
            <div className="flex flex-wrap gap-2" data-testid="modal-benefits">
              {product.benefits.map((b) => (
                <span
                  key={b}
                  className="bg-white border border-[#2B0B3C]/10 rounded-full px-4 py-1.5 text-[11px] font-bold uppercase tracking-wide text-[#2B0B3C]/70"
                >
                  {b}
                </span>
              ))}
            </div>
          )}

          {nutrition && (
            <div>
              <h4 className="font-display text-lg font-bold uppercase tracking-wide text-[#2B0B3C] mb-3">Nutrition</h4>
              <div className="bg-white rounded-2xl overflow-hidden border border-[#2B0B3C]/10" data-testid="nutrition-table">
                <div className="flex justify-between px-5 py-3 bg-[#2B0B3C] text-white text-[11px] font-bold uppercase tracking-[0.15em]">
                  <span>Typical values</span>
                  <span>{nutrition.basis}</span>
                </div>
                {nutrition.rows.map((row, i) => (
                  <div
                    key={row[0]}
                    className={`flex justify-between px-5 py-2.5 text-sm ${i % 2 ? "bg-[#FBF6EC]" : "bg-white"}`}
                    data-testid={`nutrition-row-${i}`}
                  >
                    <span className="font-medium text-[#2B0B3C]/70">{row[0]}</span>
                    <span className="font-bold text-[#2B0B3C]">{row[1]}</span>
                  </div>
                ))}
                {nutrition.extra && (
                  <div className="px-5 py-2.5 text-xs font-bold text-[#4A148C] bg-[#EEDDFF]">{nutrition.extra}</div>
                )}
              </div>
            </div>
          )}

          {product.ingredients && (
            <div>
              <h4 className="font-display text-lg font-bold uppercase tracking-wide text-[#2B0B3C] mb-2">Ingredients</h4>
              <p className="text-sm font-medium leading-relaxed text-[#2B0B3C]/70" data-testid="ingredients-list">
                {product.ingredients.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
                  part.startsWith("**") && part.endsWith("**") ? (
                    <strong key={i} className="text-[#2B0B3C]">{part.slice(2, -2)}</strong>
                  ) : (
                    part
                  )
                )}
              </p>
            </div>
          )}

          {product.allergy_advice && (
            <p
              className="text-xs font-bold leading-relaxed text-[#8A6D00] bg-[#FFF4CC] rounded-2xl px-5 py-3.5"
              data-testid="allergy-advice"
            >
              Allergy advice: {product.allergy_advice}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
